import { useTranslation } from "react-i18next";

import type { EntryExit } from "../reportTypes";
import { StateBar } from "./StateBar";
import { deltaPp } from "../format";

/**
 * Entry-vs-exit pair of stacked state bars — the core before/after motif from the
 * Population view. Shows a protective-shift delta (percentage points) as the
 * headline movement, with assessed denominators when not compact.
 */
type Props = {
  data: EntryExit;
  /** Hide the assessed-n captions for dense table rows. */
  compact?: boolean;
};

export function EntryExitBars({ data, compact = false }: Props) {
  const { t } = useTranslation();
  const shift = deltaPp(
    data.entryProportion.protective,
    data.exitProportion.protective,
  );
  const positive = !shift.startsWith("-") && shift !== "0";

  return (
    <div className="rpt-ee">
      <div className="rpt-ee__row">
        <span className="rpt-ee__phase">{t("reports.atEntry")}</span>
        <StateBar
          proportion={data.entryProportion}
          phaseLabel={t("reports.atEntry")}
        />
        {!compact && (
          <span className="rpt-ee__n">
            {t("reports.assessedN", { n: data.assessedEntry })}
          </span>
        )}
      </div>
      <div className="rpt-ee__row">
        <span className="rpt-ee__phase">{t("reports.atExit")}</span>
        <StateBar
          proportion={data.exitProportion}
          phaseLabel={t("reports.atExit")}
        />
        {!compact && (
          <span className="rpt-ee__n">
            {t("reports.assessedN", { n: data.assessedExit })}
          </span>
        )}
      </div>
      <div
        className={
          positive ? "rpt-ee__shift rpt-ee__shift--up" : "rpt-ee__shift"
        }
        title={t("reports.protectiveShiftHint")}
      >
        <span aria-hidden="true">{positive ? "▲" : ""}</span>{" "}
        {t("reports.protectiveShift", { delta: shift })}
      </div>
    </div>
  );
}

export default EntryExitBars;
